/**
 * What a Superset chart drew, in words and a table — the whole of what a screen reader gets.
 *
 * The host puts role="img" on the canvas, which prunes every node the chart renders (and its
 * hover-only tooltip) from the accessibility tree. The accessible name and the sr-only table built
 * here are therefore the chart, for anyone who cannot see it, so they are built from the props the
 * bundle reports it DREW — never from the request — and they state what is on screen: a stacked
 * bar lists its series in the order the legend does, a pie leaves out the zero slices it does not
 * draw, and a Big Number reads the headline the card shows.
 *
 * Pure: no bundle, no DOM, no Alpine. SupersetChartHarnessTest drives it under Node with the real
 * contract fixtures, and a surface that keeps one Alpine scope for many charts can call it directly.
 */
import { isFiniteNumber } from './cartesian-chrome.js';

const TIMESERIES = new Set(['echarts_timeseries_line', 'echarts_timeseries_bar', 'blink_mixed', 'blink_bar', 'blink_area']);
const BARS = new Set(['echarts_timeseries_bar', 'blink_bar']);
const DAY_MS = 86_400_000;

/**
 * The id prefix of a goal band the bundle's styling hook draws (GOAL_BAND_ID_PREFIX in
 * standalone/contract.tsx). A band is a shading of the line it belongs to, not a series with
 * values of its own, so it is no column of the table; smoke.charts.mjs holds the two prefixes
 * equal.
 */
const GOAL_BAND_ID_PREFIX = 'blink-goal-band:';

/**
 * The columns one query plots values from: every column except the x axis and the dimensions a
 * pie is grouped by.
 */
function valueColumns(contract, query) {
    const formData = contract?.formData ?? {};
    const exclude = new Set([formData.x_axis ?? '__timestamp', ...(formData.groupby ?? [])]);

    return (query?.colnames ?? []).filter((column) => ! exclude.has(column));
}

/**
 * Whether the chart would draw nothing, decided from the contract alone so an empty card never
 * downloads the bundle.
 *
 * A pie counts only values above zero: Superset drops the rest, so a pie of zeros is a blank
 * ring, not a chart. A Big Number Total whose value is null has no headline to draw. Every query
 * counts: a two-axis chart whose left-hand series have no readings still draws its right-hand ones.
 */
export function isEmptyContract(contract) {
    const counts = contract?.vizType === 'pie'
        ? (value) => isFiniteNumber(value) && value > 0
        : isFiniteNumber;

    return ! (contract?.queriesData ?? []).some((query) => {
        const columns = valueColumns(contract, query);

        return (query?.data ?? []).some((row) => columns.some((column) => counts(row[column])));
    });
}

/** The number-format descriptor the chart prints its values with, or null. */
export function valueDescriptor(contract) {
    const formData = contract?.formData ?? {};

    return contract?.numberFormats?.[formData.yAxisFormat ?? formData.numberFormat] ?? null;
}

/**
 * The descriptor a two-axis chart prints its right axis's values with, or null for a chart with
 * one axis. The table formats each series by its own axis: printed with the left axis's format,
 * a series of thousands of impressions would read in the other series' unit.
 */
export function secondaryDescriptor(contract) {
    return contract?.numberFormats?.[contract?.formData?.yAxisFormatSecondary] ?? null;
}

/**
 * A formatter for the periods a series is plotted over, named by the grain the chart is drawn at.
 *
 * Not the chart's own axis formatter: that is Superset's adaptive "smart date", which labels the
 * first of January as "2025" and every other day without its year — right for a tick, wrong for a
 * table row a reader has to identify on its own. And a point of a coarser grain is named as that
 * grain, never as its first day: a monthly row read as "1 Sep 2026" puts a month's figure beside
 * one day's name. Formatted in UTC because Superset draws these charts with useUTC: the contract's
 * timestamps are naive dates encoded as UTC midnight, and a local-time formatter would move every
 * one of them to the previous day west of Greenwich.
 *
 * `periods` is the surface's own name for each point, keyed by instant, and wins where it has one:
 * only the surface knows that a window's first month holds eight of its days. A chart drawn over
 * its periods' labels rather than their instants is named from `periods` in describeSeries().
 */
export function periodFormatter(timestamps, locale, grain = null, periods = null) {
    const zone = { timeZone: 'UTC' };
    const named = (format) => (timestamp) => periods?.[timestamp] ?? format(timestamp);

    if (grain === 'P1W') {
        const days = new Intl.DateTimeFormat(locale || undefined, { ...zone, year: 'numeric', month: 'short', day: 'numeric' });

        return named((timestamp) => days.formatRange(new Date(timestamp), new Date(timestamp + 6 * DAY_MS)));
    }

    if (grain === 'P1M') {
        const month = new Intl.DateTimeFormat(locale || undefined, { ...zone, year: 'numeric', month: 'long' });

        return named((timestamp) => month.format(new Date(timestamp)));
    }

    if (grain === 'P3M') {
        return named((timestamp) => {
            const date = new Date(timestamp);

            return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
        });
    }

    if (grain === 'P1Y') {
        return named((timestamp) => String(new Date(timestamp).getUTCFullYear()));
    }

    const withTime = timestamps.some((timestamp) => timestamp % DAY_MS !== 0);
    const format = new Intl.DateTimeFormat(locale || undefined, {
        ...zone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
    });

    return named((timestamp) => format.format(new Date(timestamp)));
}

/**
 * Line, bar or area, from what was drawn: a mixed chart can draw either of the first two, so its
 * type is read off the series rather than the viz key.
 */
function seriesTypeKey(contract, series) {
    if (BARS.has(contract.vizType)) return 'typeBar';
    if (contract.vizType === 'blink_area') return 'typeArea';
    if (contract.vizType === 'blink_mixed' && series.length > 0 && series.every((entry) => entry.type === 'bar')) return 'typeBar';

    return 'typeLine';
}

/**
 * A series is read out by the name its page gives it (`seriesNames`, label => name), else by its
 * label. Only the words change: the drawn series and their order are the chart's.
 */
function seriesName(entry, seriesNames) {
    const label = String(entry.name ?? '');
    const named = seriesNames?.[label];

    return typeof named === 'string' && named !== '' ? named : label;
}

/**
 * A drawn point as `[x, y]`. Most arrive that way; a Blink bar writes every period its opacity
 * query gives a status — "Actual" at full strength included — as `{value: [x, y], itemStyle:
 * {opacity}}`, the per-item form ECharts takes an opacity in.
 */
function pairOf(point) {
    return Array.isArray(point) ? point : point?.value;
}

/**
 * The status of each period the chart draws dimmed — a projected period beside the actual ones —
 * by instant.
 *
 * The word is the status PHP sent in the opacity query, since nothing drawn carries it. Whether the
 * period WAS dimmed is read from the drawing: a bar's own opacity, or the gradient an area is filled
 * with, which the Blink area builds from that same query. So a transform that stopped dimming would
 * stop the table from saying it does. A status drawn at full strength sets nothing apart, and is
 * left unsaid.
 */
function dimmedPeriods(contract, series) {
    const formData = contract?.formData ?? {};
    const column = formData.opacityColumn;
    if (! column) return new Map();

    const opacities = formData.opacityValueMap ?? {};
    const filled = series.some((entry) => Array.isArray(entry.areaStyle?.color?.colorStops));
    const dimmedBars = new Set(series.flatMap((entry) => entry.data
        .filter((point) => ! Array.isArray(point) && point?.itemStyle?.opacity < 1)
        .map((point) => pairOf(point)[0])));

    return new Map((contract.queriesData?.[1]?.data ?? [])
        .filter((row) => Number(opacities[row[column]]) < 1 && (filled || dimmedBars.has(row.__timestamp)))
        .map((row) => [row.__timestamp, String(row[column])]));
}

function describeSeries(contract, props, { formatValue, formatSecondary = formatValue, locale, seriesNames = null, periods = null }) {
    const horizontal = contract.formData?.orientation === 'horizontal';
    const series = (props.echartOptions?.series ?? []).filter((entry) => Array.isArray(entry.data)
        && entry.data.length > 0
        && ! String(entry.id ?? '').startsWith(GOAL_BAND_ID_PREFIX));
    // A series on the right axis prints with that axis's format.
    const formatters = series.map((entry) => (entry.yAxisIndex === 1 ? formatSecondary : formatValue));
    // A point is a pair, or an object carrying the pair and a style of its own: a bar coloured by
    // its category, a negative bar whose label moves outside it, or a period drawn dimmed.
    const point = (drawn) => {
        const pair = pairOf(drawn) ?? [];

        return horizontal ? [pair[1], pair[0]] : [pair[0], pair[1]];
    };
    const dimmed = dimmedPeriods(contract, series);

    const xs = [...new Set(series.flatMap((entry) => entry.data.map((item) => point(item)[0])))];
    const temporal = xs.every((x) => isFiniteNumber(x));
    if (temporal) xs.sort((a, b) => a - b);
    // A horizontal chart puts its first category at the bottom, so the table lists them in the
    // reverse of the order they were drawn in: top to bottom, as a reader sees them. Periods stay in
    // time order whichever way the chart is turned.
    if (! temporal && horizontal) xs.reverse();
    // A category the surface names in `periods` is a period drawn over its own label, as the metric
    // view draws them: its row takes that name ("Q 3 2026 (in progress)"), and the table and the
    // accessible name count periods, not categories.
    const namedPeriods = ! temporal && periods !== null && xs.every((x) => typeof periods[x] === 'string');
    const label = temporal
        ? periodFormatter(xs, locale, contract.formData?.timeGrainSqla ?? null, periods)
        : (x) => (namedPeriods ? periods[x] : String(x));

    const byX = series.map((entry) => new Map(entry.data.map((item) => point(item))));

    return {
        kind: 'series',
        // Whether the rows are periods, which the table's first column and the accessible name say.
        temporal: temporal || namedPeriods,
        typeKey: seriesTypeKey(contract, series),
        names: series.map((entry) => seriesName(entry, seriesNames)),
        colors: series.map((entry) => entry.itemStyle?.color ?? entry.color ?? null),
        rows: xs.map((x, index) => ({
            label: label(x),
            status: dimmed.get(x) ?? null,
            cells: byX.map((values, column) => {
                const value = values.get(xs[index]);

                return isFiniteNumber(value) ? formatters[column](value) : null;
            }),
        })),
        count: xs.length,
        headline: null,
        subheader: '',
        goal: null,
    };
}

function describeSlices(contract, props, { formatValue, formatShare = null, locale, periods = null }) {
    const slices = (props.echartOptions?.series?.[0]?.data ?? [])
        .filter((slice) => isFiniteNumber(slice.value) && slice.value > 0);
    // Slices the surface names in `periods` are periods, as the metric view's pie draws one per
    // period: each row takes that name ("Q 3 2026 (in progress)"), and the table heads them Period.
    const namedPeriods = periods !== null && slices.length > 0 && slices.every((slice) => typeof periods[slice.name] === 'string');
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    // Two decimals, as Superset's own pie tooltip prints the share — unless the surface replaced
    // that tooltip and prints its shares another way, in which case the table reads what its
    // tooltip shows.
    const share = new Intl.NumberFormat(locale || undefined, { style: 'percent', maximumFractionDigits: 2 });
    const printShare = formatShare ?? ((value) => share.format(value / total));

    return {
        kind: 'slices',
        // Whether the rows are periods, which the table's first column says.
        temporal: namedPeriods,
        typeKey: contract.formData?.donut ? 'typeDonut' : 'typePie',
        names: slices.map((slice) => String(slice.name ?? '')),
        colors: slices.map((slice) => slice.itemStyle?.color ?? null),
        rows: slices.map((slice) => ({
            label: namedPeriods ? periods[slice.name] : String(slice.name ?? ''),
            cells: [formatValue(slice.value), printShare(slice.value, total)],
        })),
        count: slices.length,
        headline: null,
        subheader: '',
        goal: null,
    };
}

/**
 * What a Big Number with Trendline drew beyond its number, as facts its label can say in words.
 *
 * The card prints its change against an earlier period as a glyph or a sign and a magnitude —
 * "▲ 9.4% YoY" on a dark card, "+9.4% YoY" on a light one — in place of its caption, and a screen
 * reader would read the glyph as the name of a shape. So the change is kept as a direction and the
 * magnitude the card printed, and whether that is better or worse when the metric says which way is
 * good. The goal's status is kept only where the card shows it: the badge on a dark card, the goal
 * line on a trend.
 *
 * Read from the drawn props: `subheader` differs from the requested one exactly when a comparison
 * was drawn, and `className` carries its direction.
 */
function bigNumberFacts(contract, props, { formatValue }, trendDrawn) {
    const formData = contract.formData ?? {};
    const drawn = String(props.subheader ?? '');
    const compared = drawn !== '' && drawn !== String(formData.subheader ?? '');
    const className = String(props.className ?? '');
    const direction = className.includes('positive') ? 'up' : className.includes('negative') ? 'down' : 'flat';
    const higherIsBetter = { higher: true, lower: false }[formData.goalDirection] ?? null;
    // A change coloured by Goal direction prints its verdict after it ("▼ 5.0% · better"). It is
    // said once, as the name's own better/worse clause, not read out a second time with the change.
    const verdict = props.changeValenceLabel ? `· ${props.changeValenceLabel}` : '';
    // The arrow (or a flat chip's dash) and the sign are the direction, which the name says in words.
    const printed = drawn.replace(/^(?:[▲▼—]\s*)?[+\-−]?/u, '').trim();

    const goalValue = formData.goalValue === '' || formData.goalValue === null ? NaN : Number(formData.goalValue);
    const goalShown = Boolean(formData.showGoalLine) && Number.isFinite(goalValue) && isFiniteNumber(props.bigNumber)
        && (Boolean(props.goalBadge) || trendDrawn);

    return {
        change: compared
            ? {
                direction,
                // Trimmed: on a light card the plugin prints `${change} ${compareSuffix}`, so a
                // change with no suffix ends in a space the name would otherwise read as "0.0% .".
                text: verdict && printed.endsWith(verdict) ? printed.slice(0, -verdict.length).trim() : printed,
                better: props.changeValence
                    ? props.changeValence === 'better'
                    : direction === 'flat' || higherIsBetter === null ? null : (direction === 'up') === higherIsBetter,
            }
            : null,
        goalStatus: goalShown
            ? {
                met: formData.goalDirection === 'lower' ? props.bigNumber <= goalValue : props.bigNumber >= goalValue,
                value: formatValue(goalValue),
            }
            : null,
    };
}

const NO_FACTS = { change: null, goalStatus: null };

/**
 * Big Number with Trendline. The headline is `bigNumber`, which the plugin has already resolved
 * to the latest non-null point — a trailing null period shows the value before it, not "no data".
 * A Big Number Total carries its caption as `subtitle`, the trendline variant as `subheader`, which
 * a drawn comparison replaces.
 */
function describeTrend(contract, props, options) {
    const { formatValue, locale } = options;
    const trend = props.trendLineData ?? [];
    const label = periodFormatter(trend.map(([timestamp]) => timestamp), locale, contract.formData?.timeGrainSqla ?? null, options.periods ?? null);
    const formData = contract.formData ?? {};
    const facts = bigNumberFacts(contract, props, options, true);

    return {
        kind: 'trend',
        typeKey: null,
        names: [],
        colors: [],
        rows: trend.map(([timestamp, value]) => ({
            label: label(timestamp),
            cells: [isFiniteNumber(value) ? formatValue(value) : null],
        })),
        count: trend.length,
        headline: isFiniteNumber(props.bigNumber) ? formatValue(props.bigNumber) : null,
        subheader: String((facts.change ? props.subtitle : props.subheader || props.subtitle) || ''),
        goal: formData.showGoalLine && isFiniteNumber(formData.goalValue)
            ? { label: formData.goalLabel ? String(formData.goalLabel) : null, value: formatValue(formData.goalValue) }
            : null,
        ...facts,
    };
}

/**
 * One figure: a Big Number Total, or a Big Number with Trendline whose line is switched off (the
 * plain Big Number card), which still draws its comparison and, on a dark card, its goal badge.
 */
function describeHeadline(contract, props, options) {
    const facts = contract.vizType === 'big_number' ? bigNumberFacts(contract, props, options, false) : NO_FACTS;

    return {
        kind: 'headline',
        typeKey: null,
        names: [],
        colors: [],
        rows: [],
        count: 1,
        headline: isFiniteNumber(props.bigNumber) ? options.formatValue(props.bigNumber) : null,
        subheader: String((facts.change ? props.subtitle : props.subtitle || props.subheader) || ''),
        goal: null,
        ...facts,
    };
}

/**
 * A gauge: one figure on a ring, printed in its middle, with the caption Blink's gauge draws above
 * it (`centerTitle`). The figure is read from the drawn series, which the ring's arc is measured
 * from, and printed in the gauge's own format, as its middle prints it. No table: the accessible
 * name says everything the ring does.
 */
function describeGauge(contract, props, { formatValue }) {
    const item = props.echartOptions?.series?.[0]?.data?.[0];

    return {
        kind: 'gauge',
        typeKey: 'typeGauge',
        names: [],
        colors: [],
        rows: [],
        count: 1,
        headline: isFiniteNumber(item?.value) ? formatValue(item.value) : null,
        subheader: String(contract.formData?.centerTitle ?? '').trim(),
        goal: null,
        ...NO_FACTS,
    };
}

/**
 * @param {object}   contract           The contract that was drawn.
 * @param {object}   props              What mountBlinkChart() reports it drew.
 * @param {object}   options
 * @param {Function} options.formatValue The bundle's own formatter for the contract's descriptor.
 * @param {Function} [options.formatSecondary] The same for a two-axis chart's right axis.
 * @param {Function} [options.formatShare] (value, total) => a slice's share as the surface's own
 *                                      tooltip prints it; omitted, Superset's two decimals.
 * @param {string}   [options.locale]   The page's language, for dates and shares.
 * @param {?object}  [options.seriesNames] Series label => the name a timeseries' table and
 *                                      accessible name read it by; a label left out reads as itself.
 * @param {?object}  [options.periods] X value => the name the accessible table gives that point:
 *                                      an instant, where the surface names it better than the
 *                                      chart's grain, or a category that is a period drawn over its
 *                                      own label, which the table then reads as a period.
 */
export function describeDrawn(contract, props, options) {
    if (TIMESERIES.has(contract.vizType)) return describeSeries(contract, props, options);
    if (contract.vizType === 'pie') return describeSlices(contract, props, options);
    if (contract.vizType === 'big_number') {
        // A card with its line switched off draws no trend, so it has no table to promise.
        return (props.trendLineData ?? []).length
            ? describeTrend(contract, props, options)
            : describeHeadline(contract, props, options);
    }
    if (contract.vizType === 'big_number_total') return describeHeadline(contract, props, options);
    if (contract.vizType === 'blink_gauge') return describeGauge(contract, props, options);

    // Reached only when a viz gains a typed arm in the bundle without a branch here. Throwing
    // turns the card into its failed state, which is louder than a chart a screen reader cannot
    // read at all — the accessible equivalent is not optional.
    throw new Error(`No accessible description for ${contract.vizType}: add a branch to superset-chart-describe.js.`);
}

/**
 * One pass over a translated template, replacing tokens through a function: a title is arbitrary
 * user text, so chained .replace() would interpret `$&` inside it and let a title containing
 * ":count" swallow the real placeholder. Unknown or missing tokens collapse to nothing, and the
 * doubled space an empty token leaves is folded away.
 */
export function fill(template, values) {
    return String(template ?? '')
        .replace(/:title|:type|:series|:count|:value|:subheader|:status|:change|:goal|:period/g, (token) => {
            const value = values[token];

            return value === undefined || value === null ? '' : String(value);
        })
        .replace(/\s{2,}/g, ' ')
        .trim();
}

/**
 * A Big Number's change and goal status, each as its own translated sentence. The better/worse
 * wording needs the metric's direction; without one, the change is only up or down.
 */
export function statusSentences(model, text) {
    const sentences = [];
    const { change, goalStatus } = model;

    if (change) {
        const valence = change.better === null ? '' : change.better ? 'Better' : 'Worse';
        const key = { up: `changeUp${valence}`, down: `changeDown${valence}`, flat: 'changeFlat' }[change.direction];
        sentences.push(fill(text[key], { ':change': change.text }));
    }
    if (goalStatus) {
        sentences.push(fill(goalStatus.met ? text.goalMet : text.goalMissed, { ':goal': goalStatus.value }));
    }

    return sentences.join(' ');
}

export function ariaLabel(model, text, title) {
    const values = {
        ':title': title,
        ':type': model.typeKey ? text[model.typeKey] : '',
        ':series': model.names.join(', '),
        ':count': model.count,
        ':value': model.headline ?? text.noDataCell,
        ':status': statusSentences(model, text),
        ':subheader': model.subheader,
    };
    const template = {
        series: model.temporal === false ? text.ariaCategories : text.ariaSeries,
        slices: text.ariaSlices,
        trend: text.ariaTrend,
        headline: text.ariaHeadline,
        gauge: text.ariaGauge,
    }[model.kind];

    return fill(template, values);
}

export function caption(text, title) {
    return fill(text.caption, { ':title': title });
}

export function tableColumns(model, text) {
    if (model.kind === 'series') return [model.temporal === false ? text.category : text.period, ...model.names];
    if (model.kind === 'slices') return [model.temporal ? text.period : text.category, text.value, text.share];
    if (model.kind === 'trend') return [text.period, text.value];

    return [];
}

export function tableRows(model, text) {
    const rows = model.rows.map((row) => ({
        // A dimmed period is set apart by opacity alone on the canvas, so its row says why.
        label: row.status ? fill(text.periodStatus, { ':period': row.label, ':status': row.status }) : row.label,
        cells: row.cells.map((cell) => cell ?? text.noDataCell),
    }));

    if (model.goal) {
        rows.push({ label: model.goal.label ?? text.goal, cells: [model.goal.value] });
    }

    return rows;
}
